// 
// Tide Protocol - Infrastructure for a TRUE Zero-Trust paradigm
// Copyright (C) 2022 Tide Foundation Ltd
// 
// This program is free software and is subject to the terms of 
// the Tide Community Open Code License as published by the 
// Tide Foundation Limited. You may modify it and redistribute 
// it in accordance with and subject to the terms of that License.
// This program is distributed WITHOUT WARRANTY of any kind, 
// including without any implied warranty of MERCHANTABILITY or 
// FITNESS FOR A PARTICULAR PURPOSE.
// See the Tide Community Open Code License for more details.
// You should have received a copy of the Tide Community Open 
// Code License along with this program.
// If not, see https://tide.org/licenses_tcoc2-0-0-en
//


import { Cryptide, Tools, Models, Errors } from "@tideorg/js";

const { HMAC_forHashing, SHA256_Digest, SHA512_Digest } = Cryptide.Hashing.Hash;
const { CurrentTime, randBetween } = Tools;
const { ConcatUint8Arrays, Bytes2Hex, bytesToBase64, BigIntFromByteArray, StringToUint8Array, StringFromUint8Array } = Cryptide.Serialization;
const { Min, median, mod, mod_inv } = Cryptide.Math;
const AES = Cryptide.Encryption.AES;
const DH = Cryptide.Encryption.DH;
const EdDSA = Cryptide.Signing.EdDSA;
const ElGamal = Cryptide.Encryption.ElGamal;
const Interpolation = Cryptide.Interpolation;
const Hash = Cryptide.Hashing.Hash;
const Math = Cryptide.Math;
const { genBlindMessage, serializeBlindSig, unblindSignature, verifyBlindSignature } = Cryptide.Signing.BlindSig;
const { Point } = Cryptide.Ed25519;
const Ed25519PublicComponent = Cryptide.Components.Schemes.Ed25519.Ed25519PublicComponent;
const TideKey = Cryptide.TideKey;
const { BaseComponent } = Cryptide.Components;
const AuthRequest = Models.AuthRequest;
const VendorData = Models.VendorData;

import PrismConvertResponse from "../Models/KeyAuth/Convert/PrismConvertResponse.js";
import DecryptedCMKConvertResponse from "../Models/KeyAuth/Convert/DecryptedCMKConvertResponse.js";
import CMKConvertResponse from "../Models/KeyAuth/Convert/CMKConvertResponse.js";
import DecryptedPrismConvertResponse from "../Models/KeyAuth/Convert/DecryptedPrismConvertResponse.js";
import AuthenticateResponse from "../Models/KeyAuth/Authenticate/AuthenticateResponse.js";
import DecryptedConvertRememberedResponse from "../Models/KeyAuth/Convert/DecryptedConvertRememberedResponse.js";
import ConvertRememberedResponse from "../Models/KeyAuth/Convert/ConvertRememberedResponse.js";
import DeviceConvertResponse from "../Models/KeyAuth/Convert/DeviceConvertResponse.js";
import DecryptedDeviceConvertResponse from "../Models/KeyAuth/Convert/DecryptedDeviceConvertResponse.js";
/**
 * For use in change password flow
 * @param {PrismConvertResponse[]} convertResponses 
 * @param {bigint[]} lis 
 * @param {Point[]} mgORKi 
 * @param {bigint} r1 
 * @returns 
 */
export async function GetDecryptedChallenge(convertResponses, lis, mgORKi, r1){
    const gPassPRISM = convertResponses.reduce((sum, next, i) => sum.add(next.GBlurPassPrismi.mul(lis[i])), Point.ZERO).mul(mod_inv(r1));
    const gPassPRISM_hashed = mod(BigIntFromByteArray(await SHA256_Digest(gPassPRISM.toRawBytes())));

    const pre_prismAuthi = mgORKi.map(async ork => await SHA256_Digest(ork.mul(gPassPRISM_hashed).toRawBytes())) // create a prismAuthi for each ork
    const prismAuthis = await Promise.all(pre_prismAuthi); // wait for all async functions to finish

    let decryptedChallenges;
    try{
        const pre_decData = convertResponses.map(async (resp, i) => await AES.decryptData(resp.EncChallengei, prismAuthis[i]));
        decryptedChallenges = await Promise.all(pre_decData);
    }catch(ex){
        throw new Errors.TideError({
            code: Errors.TideJsErrorCodes.VAL_INVALID_ACCOUNT,
            displayMessage: "enclave.invalidAccount",
            messageKey: "enclave.invalidAccount",
            source: "Ork/Enclave/js/Math/KeyAuthentication.js:69 (GetDecryptedChallenge)",
            details: [{
                endpoint: "GetDecryptedChallenge",
                displayMessage: `AES.decryptData of EncChallengei failed across ${convertResponses?.length ?? "<missing>"} ORK responses (likely wrong PRISM-derived key -> wrong password). Used in change-password flow.`,
            }],
            cause: ex,
        });
    }

    return decryptedChallenges;
}

/**
 * @param {PrismConvertResponse[]} convertResponses 
 * @param {bigint[]} ids
 * @param {Point[]} mgORKi 
 * @param {bigint} r1 
 * @param {Uint8Array[]} prkECDHi
 */
export async function PrismConvertReply(convertResponses, ids, mgORKi, r1, prkECDHi){    
    // ∑ gPass ⋅ r1 ⋅ PRISMi ⋅ li / r1
    const gPassPRISM = Interpolation.AggregatePointsWithIds(convertResponses.map(resp => resp.GBlurPassPrismi), ids).divide(r1);
    const gPassPRISM_hashed = await gPassPRISM.hash();

    const prismAuthis = await DH.generateECDHi(mgORKi, gPassPRISM_hashed);

    let decPrismRequesti;
    try{
        const pre_decPrismRequesti = convertResponses.map(async (chall, i) => DecryptedPrismConvertResponse.from(await AES.decryptData(chall.EncRequesti, prismAuthis[i])));
        decPrismRequesti = await Promise.all(pre_decPrismRequesti);
    }catch(ex){
        throw new Errors.TideError({
            code: Errors.TideJsErrorCodes.VAL_INVALID_ACCOUNT,
            displayMessage: "enclave.invalidAccount",
            messageKey: "enclave.invalidAccount",
            source: "Ork/Enclave/js/Math/KeyAuthentication.js:104 (PrismConvertReply)",
            details: [{
                endpoint: "PrismConvertReply",
                displayMessage: `AES.decryptData of EncRequesti failed across ${convertResponses?.length ?? "<missing>"} ORK responses (likely wrong PRISM-derived key -> wrong password). Used in password sign-in convert step.`,
            }],
            cause: ex,
        });
    }
    const timestampi = median(decPrismRequesti.map(resp => resp.Timestampi));
    window.localStorage.setItem("timeSkew", String(timestampi - BigInt(window.Math.floor(Date.now() / 1000)))) // Update time skew value on this enclave

    const pre_selfRequesti = decPrismRequesti.map(async (req, i) => await AES.decryptData(req.PRKRequesti, prkECDHi[i]));
    const selfRequesti = await Promise.all(pre_selfRequesti);

    // Calculate when the stored token expires
    const expired = CurrentTime() + Min(decPrismRequesti.map(d => d.Exti));

    return {prismAuthis, timestampi, selfRequesti, expired}
}
/**
 * @param {PrismConvertResponse[]} convertResponses 
 * @param {bigint[]} ids
 * @param {Point[]} mgORKi 
 * @param {bigint} r1 
 */
export async function DevicePrismConvertReply(convertResponses, ids, mgORKi, r1){    
    // ∑ gPass ⋅ r1 ⋅ PRISMi ⋅ li / r1
    const gPassPRISM = Interpolation.AggregatePointsWithIds(convertResponses.map(resp => resp.GBlurPassPrismi), ids).divide(r1);
    const gPassPRISM_hashed = await gPassPRISM.hash();

    const prismAuthis = await DH.generateECDHi(mgORKi, gPassPRISM_hashed);

    let decPrismRequesti;
    try{
        const pre_decPrismRequesti = convertResponses.map(async (chall, i) => DecryptedPrismConvertResponse.from(await AES.decryptData(chall.EncRequesti, prismAuthis[i])));
        decPrismRequesti = await Promise.all(pre_decPrismRequesti);
    }catch(ex){
        throw new Errors.TideError({
            code: Errors.TideJsErrorCodes.VAL_INVALID_ACCOUNT,
            displayMessage: "enclave.invalidAccount",
            messageKey: "enclave.invalidAccount",
            source: "Ork/Enclave/js/Math/KeyAuthentication.js:145 (DevicePrismConvertReply)",
            details: [{
                endpoint: "DevicePrismConvertReply",
                displayMessage: `AES.decryptData of EncRequesti failed across ${convertResponses?.length ?? "<missing>"} ORK responses (likely wrong PRISM-derived key -> wrong password). Used in device convert step.`,
            }],
            cause: ex,
        });
    }
    const timestampi = median(decPrismRequesti.map(resp => resp.Timestampi));
    window.localStorage.setItem("timeSkew", String(timestampi - BigInt(window.Math.floor(Date.now() / 1000)))) // Update time skew value on this enclave

    // Calculate when the stored token expires
    const expired = CurrentTime() + Min(decPrismRequesti.map(d => d.Exti));

    return {prismAuthis, timestampi, prkRequesti: decPrismRequesti.map(d => d.PRKRequesti), expired}
}

/**
 * @param {CMKConvertResponse[]} convertResponses 
 * @param {bigint[]} ids
 * @param {Uint8Array[]} prismAuthis
 * @param {Point} gCMK 
 * @param {bigint} timestampi
 * @param {string} sessID
 * @param {string} purpose
 * @param {Point} qPub
 * @param {bigint} uDeObf
 * @param {bigint} blurerKPriv
 * @param {Ed25519PublicComponent} gSessKeyPub
 * @param {string|undefined} dPoPApproval
 */
export async function CmkConvertReply(convertResponses, ids, prismAuthis, gCMK, timestampi, sessID, purpose, qPub, uDeObf, blurerKPriv, gSessKeyPub, dPoPApproval){
    let decData;
    try{
        const pre_decData = convertResponses.map(async (resp, i) => DecryptedCMKConvertResponse.from(await AES.decryptData(resp.EncChallengei, prismAuthis[i])));
        decData = await Promise.all(pre_decData);
    }catch(ex){
        throw new Errors.TideError({
            code: Errors.TideJsErrorCodes.VAL_INVALID_ACCOUNT,
            displayMessage: "enclave.invalidAccount",
            messageKey: "enclave.invalidAccount",
            source: "Ork/Enclave/js/Math/KeyAuthentication.js:186 (CmkConvertReply)",
            details: [{
                endpoint: "CmkConvertReply",
                displayMessage: `AES.decryptData of EncChallengei failed across ${convertResponses?.length ?? "<missing>"} ORK responses (likely wrong PRISM-derived key -> wrong password). purpose=${purpose ?? "<missing>"} sessID=${sessID ? sessID.slice(0, 16) + "..." : "<missing>"}`,
            }],
            cause: ex,
        });
    }

    const userPRISM = Interpolation.AggregatePointsWithIds(decData.map(d => d.UserPRISMi), ids);
    const userPRISMdec = userPRISM.mul(mod(BigIntFromByteArray(await DH.computeSharedKey(qPub, blurerKPriv))));

    const gUserCMK = userPRISMdec.divide(uDeObf);
    const gUserCMK_Hash = await Hash.SHA512_Digest(gUserCMK.toRawBytes());

    const CMKMul = mod(BigIntFromByteArray(gUserCMK_Hash.slice(0, 32)));
    const VUID = Bytes2Hex(gUserCMK_Hash.slice(-32));
    const gCMKAuth = gCMK.mul(CMKMul);
    const gCMKR = Interpolation.AggregatePoints(convertResponses.map(resp => resp.GCMKRi));
    const authToken = AuthRequest.new(VUID, purpose, gSessKeyPub.Serialize().ToString(), timestampi + randBetween(30, 90), sessID, dPoPApproval);
    const {blurHCMKMul, blur, gRMul} = await genBlindMessage(gCMKR, gCMKAuth, authToken.toUint8Array(), CMKMul);

    return {VUID: VUID, blurHCMKMul, r4: blur, gCMKAuth, authToken, gRMul}
}

/**
 * @param {Uint8Array[]} encRequesti
 * @param {Uint8Array[]} appAuthi
 * @param {bigint[]} ids
 * @param {Point} gCMK
 * @param {string} qPub
 * @param {string} uDeObf
 * @param {TideKey} blurerKPriv
 * @param {Ed25519PublicComponent} gSessKeyPub
 * @param {string} purpose
 * @param {string} sessionId
 * @param {Point} gCMKR
 */
// TIDE-AUTHENTICATOR PATCH: trailing dPoPApproval param (threaded from the
// enclave via the mobile tunnel when the client is DPoP-bound; serializes as
// "" in the AuthRequest when absent, matching the ORK's C# re-serialization).
export async function DeviceConvertReply(encRequesti, appAuthi, ids, gCMK, qPub, uDeObf, blurerKPriv, gSessKeyPub, purpose, sessionId, gCMKR, dPoPApproval=null){
    let decPrismRequesti;
    try{
        const pre_decPrismRequesti = encRequesti.map(async (chall, i) => DecryptedDeviceConvertResponse.from(StringFromUint8Array(await AES.decryptDataRawOutput(chall, appAuthi[i]))));
        decPrismRequesti = await Promise.all(pre_decPrismRequesti);
    }catch(ex){
        throw new Errors.TideError({
            code: Errors.TideJsErrorCodes.VAL_INVALID_ACCOUNT,
            displayMessage: "enclave.invalidAccount",
            messageKey: "enclave.invalidAccount",
            source: "Ork/Enclave/js/Math/KeyAuthentication.js:236 (DeviceConvertReply)",
            details: [{
                endpoint: "DeviceConvertReply",
                displayMessage: `AES.decryptDataRawOutput of device-encrypted request failed across ${encRequesti?.length ?? "<missing>"} ORK responses (likely wrong appAuth-derived key). purpose=${purpose ?? "<missing>"} sessionId=${sessionId ? sessionId.slice(0, 16) + "..." : "<missing>"}`,
            }],
            cause: ex,
        });
    }
    const timestampi = median(decPrismRequesti.map(resp => resp.Timestampi));
    window.localStorage.setItem("timeSkew", String(timestampi - BigInt(window.Math.floor(Date.now() / 1000)))) // Update time skew value on this enclave

    // Calculate when the stored token expires
    const expired = CurrentTime() + Min(decPrismRequesti.map(d => d.Exti));

    // CMK part
    const userPRISM = Interpolation.AggregatePointsWithIds(decPrismRequesti.map(d => d.UserPRISMi), ids);
    const userPRISMdec = userPRISM.mul(mod(BigIntFromByteArray(await DH.computeSharedKey(TideKey.FromSerializedComponent(qPub).get_public_component().public, blurerKPriv.get_private_component().priv))));

    const gUserCMK = userPRISMdec.divide(TideKey.FromSerializedComponent(uDeObf).get_private_component().priv);
    const gUserCMK_Hash = await Hash.SHA512_Digest(gUserCMK.toRawBytes());

    const CMKMul = mod(BigIntFromByteArray(gUserCMK_Hash.slice(0, 32)));
    const VUID = Bytes2Hex(gUserCMK_Hash.slice(-32));
    const gCMKAuth = gCMK.mul(CMKMul);
    const authToken = AuthRequest.new(VUID, purpose, gSessKeyPub.Serialize().ToString(), timestampi + randBetween(30, 90), sessionId, dPoPApproval); // TIDE-AUTHENTICATOR PATCH
    const {blurHCMKMul, blur, gRMul} = await genBlindMessage(gCMKR, gCMKAuth, authToken.toUint8Array(), CMKMul);

    return {VUID, gCMKAuth, authToken, r4: blur, decPrismRequesti, timestampi, expired, blurHCMKMul, gRMul}
}
/**
 * @param {ConvertRememberedResponse[]} responses 
 * @param {bigint[]} mIdORKi 
 * @param {Point} gCMK 
 * @param {string} sessID 
 * @param {Uint8Array[]} prkECDHi
 * @param {Point} qPub
 * @param {bigint} uDeObf
 * @param {bigint} blurerKPriv
 * @param {Ed25519PublicComponent} gSessKeyPub
 * @param {string|undefined} dPoPApproval
 */
export async function ConvertRememberedReply(responses, mIdORKi, gCMK, sessID, prkECDHi, qPub, uDeObf, blurerKPriv, gSessKeyPub, dPoPApproval){
    const pre_decryptedResonses = responses.map((async(resp, i) => DecryptedConvertRememberedResponse.from(await AES.decryptData(resp.EncRequesti, prkECDHi[i]))));
    const decryptedResponses = await Promise.all(pre_decryptedResonses);

    const timestamp = Math.median(decryptedResponses.map(d => d.timestampi));
    window.localStorage.setItem("timeSkew", String(timestamp - BigInt(window.Math.floor(Date.now() / 1000)))) // Update time skew value on this enclave

    const userPRISM = Interpolation.AggregatePointsWithIds(decryptedResponses.map(d => d.UserPRISMi), mIdORKi);
    const userPRISMdec = userPRISM.mul(mod(BigIntFromByteArray(await DH.computeSharedKey(qPub, blurerKPriv))));

    const gUserCMK = userPRISMdec.divide(uDeObf);
    const gUserCMK_Hash = await Hash.SHA512_Digest(gUserCMK.toRawBytes());

    const CMKMul = mod(BigIntFromByteArray(gUserCMK_Hash.slice(0, 32)));
    const VUID = Bytes2Hex(gUserCMK_Hash.slice(-32));
    const gCMKAuth = gCMK.mul(CMKMul);
    const gCMKR = Interpolation.AggregatePoints(responses.map(resp => resp.GCMKRi));

    const authToken = AuthRequest.new(VUID, "auth", gSessKeyPub.Serialize().ToString(), timestamp + randBetween(30, 90), sessID, dPoPApproval);

    const {blurHCMKMul, blur: r4, gRMul} = await genBlindMessage(gCMKR, gCMKAuth, authToken.toUint8Array(), CMKMul);
    return {
        VUID,
        gCMKAuth,
        blurHCMKMul,
        r4,
        gRMul,
        authToken,
        prkECDHi
    }
}
/**
 * 
 * @param {string} vuid 
 * @param {Uint8Array[]} prkECDHi
 * @param {string[]} encSigi 
 * @param {Point} gCMKAuth 
 * @param {AuthRequest} authToken 
 * @param {bigint} r4 
 * @param {Point} gRMul 
 * @param {Point} gVRK
 */
export async function AuthenticateBasicReply(vuid, prkECDHi, encSigi, gCMKAuth, authToken, r4, gRMul, gVRK){
    const pre_authResp = encSigi.map(async (enc, i) => AuthenticateResponse.from(await AES.decryptData(enc, prkECDHi[i])));
    const authResp = await Promise.all(pre_authResp);

    const blindS = mod(authResp.reduce((sum, next) => sum + next.Si, BigInt(0)));
    const sig = await unblindSignature(blindS, r4);
    const blindSigValid = await verifyBlindSignature(sig, gRMul, gCMKAuth, authToken.toUint8Array());
    if(!blindSigValid) throw new Errors.TideError({
        code: Errors.TideJsErrorCodes.SIG_BLIND_VERIFY_FAILED,
        displayMessage: "We couldn't complete sign-in because a security check (signature verification) didn't pass. Please try signing in again. If the problem persists, contact support.",
        source: "Ork/Enclave/js/Math/KeyAuthentication.js:330 (AuthenticateBasicReply)",
        details: [{
            endpoint: "AuthenticateBasicReply",
            displayMessage: `Blind signature verification failed during basic (password) authentication. vuid=${vuid ? vuid.slice(0, 16) + "..." : "<missing>"} purpose=${authToken?.purpose ?? "<missing>"} sessionId=${authToken?.sessionId ? authToken.sessionId.slice(0, 16) + "..." : "<missing>"} orkResponseCount=${encSigi?.length ?? "<missing>"}`,
        }],
    });
    const blindSig = bytesToBase64(serializeBlindSig(sig, gRMul));

    if(gVRK == null){
        const vendorData = new VendorData(vuid, gCMKAuth, blindSig, authToken).toString();
        return vendorData;
    }else{
        const VendorEncryptedData = await ElGamal.encryptData(StringToUint8Array(new VendorData(vuid, gCMKAuth, blindSig, authToken).toString()), gVRK);
        return VendorEncryptedData;
    }
}
/**
 * 
 * @param {string} vuid 
 * @param {Uint8Array} sig 
 * @param {Point} gCMKAuth 
 * @param {AuthRequest} authToken 
 * @param {bigint} r4 
 * @param {Point} gRMul 
 * @param {Point} gVRK
 */
export async function AuthenticateDeviceReply(vuid, sig, gCMKAuth, authToken, r4, gRMul, gVRK){
    const blindS = BigIntFromByteArray(sig.slice(-32));
    const usig = await unblindSignature(blindS, r4);
    const blindSigValid = await verifyBlindSignature(usig, gRMul, gCMKAuth, authToken.toUint8Array());
    if(!blindSigValid) throw new Errors.TideError({
        code: Errors.TideJsErrorCodes.SIG_BLIND_VERIFY_FAILED,
        displayMessage: "We couldn't complete sign-in because a security check (signature verification) didn't pass. Please try signing in again. If the problem persists, contact support.",
        source: "Ork/Enclave/js/Math/KeyAuthentication.js:363 (AuthenticateDeviceReply)",
        details: [{
            endpoint: "AuthenticateDeviceReply",
            displayMessage: `Blind signature verification failed during device (passwordless) authentication. vuid=${vuid ? vuid.slice(0, 16) + "..." : "<missing>"} purpose=${authToken?.purpose ?? "<missing>"} sessionId=${authToken?.sessionId ? authToken.sessionId.slice(0, 16) + "..." : "<missing>"} sigBytes=${sig?.length ?? "<missing>"}`,
        }],
    });
    const blindSig = bytesToBase64(serializeBlindSig(usig, gRMul));

    if(gVRK == null){
        const vendorData = new VendorData(vuid, gCMKAuth, blindSig, authToken).toString();
        return vendorData;
    }else{
        const VendorEncryptedData = await ElGamal.encryptData(StringToUint8Array(new VendorData(vuid, gCMKAuth, blindSig, authToken).toString()), gVRK);
        return VendorEncryptedData;
    }
}